/**
 * unitDefinitions.ts
 * -----------------------------------------------------------------------------
 * Single source of truth for the platform's unit-of-measure categories.
 *
 * Consumed by BOTH:
 *   - backend property validation (packages/utils/spheres validation/unitConversion.ts)
 *   - the design-system UnitValueInputAtom (unit switcher + display conversion)
 * so the two sides can never drift (they previously kept separate TIME_UNITS
 * tables, which diverged: the UI knew months/years, the server did not).
 *
 * Conversion model is AFFINE: canonical = value * factor + offset.
 * Plain physical quantities use offset 0; temperature needs the offset
 * (°F → °C is not a pure ratio). Currency is deliberately NOT here — money
 * needs live FX rates, not fixed factors (it is a `number` +
 * centerCurrencyInputComponent, never `type: 'unit'`).
 *
 * Canonical units are pragmatic display-scale units (h, km, kg, L…), matching
 * the pre-existing time convention (canonical 'h', not the SI 's') — stored
 * `canonicalValue`s stay human-scaled and existing time data stays valid.
 *
 * Volume ships US customary symbols (gal = 3.785 L). British imperial variants
 * (imp gal = 4.546 L) are ~20% different — if ever needed they must be added as
 * DISTINCT symbols (e.g. 'imp gal'), never by re-meaning 'gal' per locale.
 */

export interface UnitDefinition {
  /** Multiplicative factor to canonical: canonical = value * factor + offset */
  factor: number;
  /** Additive offset applied after the factor (temperature); defaults to 0 */
  offset?: number;
  /** i18n key for the full unit name (e.g. "units.distance.kilometers") */
  labelKey: string;
  /** i18n key for the short form shown on the switcher (e.g. "units.distance.kilometersAbbrev") */
  abbrevKey: string;
  /** Alternate spellings resolved to this symbol (matched lowercased/trimmed) */
  aliases?: string[];
}

export interface UnitCategoryDefinition {
  /** The unit `canonicalValue` is stored in for this category */
  canonicalUnit: string;
  /** Unit symbol → definition. Symbol casing is display casing (mL, °C). */
  units: Record<string, UnitDefinition>;
}

export const UNIT_CATEGORIES: Record<string, UnitCategoryDefinition> = {
  time: {
    canonicalUnit: 'h',
    units: {
      s: { factor: 1 / 3600, labelKey: 'units.time.seconds', abbrevKey: 'units.time.secondsAbbrev', aliases: ['sec', 'secs', 'second', 'seconds'] },
      // 'm' resolves to minutes ONLY inside the time category (legacy behaviour);
      // in the distance category 'm' is meters. Alias resolution is per-category.
      min: { factor: 1 / 60, labelKey: 'units.time.minutes', abbrevKey: 'units.time.minutesAbbrev', aliases: ['m', 'mins', 'minute', 'minutes'] },
      h: { factor: 1, labelKey: 'units.time.hours', abbrevKey: 'units.time.hoursAbbrev', aliases: ['hr', 'hrs', 'hour', 'hours'] },
      d: { factor: 24, labelKey: 'units.time.days', abbrevKey: 'units.time.daysAbbrev', aliases: ['day', 'days'] },
      w: { factor: 24 * 7, labelKey: 'units.time.weeks', abbrevKey: 'units.time.weeksAbbrev', aliases: ['wk', 'wks', 'week', 'weeks'] },
      // Mean month/year lengths — match the factors the UI has always used.
      mo: { factor: 24 * 30.44, labelKey: 'units.time.months', abbrevKey: 'units.time.monthsAbbrev', aliases: ['month', 'months'] },
      y: { factor: 24 * 365.25, labelKey: 'units.time.years', abbrevKey: 'units.time.yearsAbbrev', aliases: ['yr', 'yrs', 'year', 'years'] },
    },
  },

  distance: {
    canonicalUnit: 'km',
    units: {
      mm: { factor: 1e-6, labelKey: 'units.distance.millimeters', abbrevKey: 'units.distance.millimetersAbbrev', aliases: ['millimeter', 'millimeters', 'millimetre', 'millimetres'] },
      cm: { factor: 1e-5, labelKey: 'units.distance.centimeters', abbrevKey: 'units.distance.centimetersAbbrev', aliases: ['centimeter', 'centimeters', 'centimetre', 'centimetres'] },
      m: { factor: 1e-3, labelKey: 'units.distance.meters', abbrevKey: 'units.distance.metersAbbrev', aliases: ['meter', 'meters', 'metre', 'metres'] },
      km: { factor: 1, labelKey: 'units.distance.kilometers', abbrevKey: 'units.distance.kilometersAbbrev', aliases: ['kms', 'kilometer', 'kilometers', 'kilometre', 'kilometres'] },
      in: { factor: 0.0000254, labelKey: 'units.distance.inches', abbrevKey: 'units.distance.inchesAbbrev', aliases: ['inch', 'inches'] },
      ft: { factor: 0.0003048, labelKey: 'units.distance.feet', abbrevKey: 'units.distance.feetAbbrev', aliases: ['foot', 'feet'] },
      yd: { factor: 0.0009144, labelKey: 'units.distance.yards', abbrevKey: 'units.distance.yardsAbbrev', aliases: ['yard', 'yards'] },
      mi: { factor: 1.609344, labelKey: 'units.distance.miles', abbrevKey: 'units.distance.milesAbbrev', aliases: ['mile', 'miles'] },
    },
  },

  speed: {
    canonicalUnit: 'km/h',
    units: {
      'km/h': { factor: 1, labelKey: 'units.speed.kilometersPerHour', abbrevKey: 'units.speed.kilometersPerHourAbbrev', aliases: ['kmh', 'kph'] },
      'm/s': { factor: 3.6, labelKey: 'units.speed.metersPerSecond', abbrevKey: 'units.speed.metersPerSecondAbbrev', aliases: ['mps'] },
      mph: { factor: 1.609344, labelKey: 'units.speed.milesPerHour', abbrevKey: 'units.speed.milesPerHourAbbrev', aliases: ['mi/h'] },
      kn: { factor: 1.852, labelKey: 'units.speed.knots', abbrevKey: 'units.speed.knotsAbbrev', aliases: ['kt', 'kts', 'knot', 'knots'] },
    },
  },

  mass: {
    canonicalUnit: 'kg',
    units: {
      g: { factor: 1e-3, labelKey: 'units.mass.grams', abbrevKey: 'units.mass.gramsAbbrev', aliases: ['gram', 'grams'] },
      kg: { factor: 1, labelKey: 'units.mass.kilograms', abbrevKey: 'units.mass.kilogramsAbbrev', aliases: ['kilo', 'kilos', 'kilogram', 'kilograms'] },
      t: { factor: 1000, labelKey: 'units.mass.tonnes', abbrevKey: 'units.mass.tonnesAbbrev', aliases: ['tonne', 'tonnes', 'metric ton'] },
      oz: { factor: 0.028349523125, labelKey: 'units.mass.ounces', abbrevKey: 'units.mass.ouncesAbbrev', aliases: ['ounce', 'ounces'] },
      lb: { factor: 0.45359237, labelKey: 'units.mass.pounds', abbrevKey: 'units.mass.poundsAbbrev', aliases: ['lbs', 'pound', 'pounds'] },
      st: { factor: 6.35029318, labelKey: 'units.mass.stones', abbrevKey: 'units.mass.stonesAbbrev', aliases: ['stone', 'stones'] },
      // US short ton — distinct from the metric tonne 't' above.
      ton: { factor: 907.18474, labelKey: 'units.mass.shortTons', abbrevKey: 'units.mass.shortTonsAbbrev', aliases: ['tons', 'short ton', 'short tons'] },
    },
  },

  volume: {
    canonicalUnit: 'L',
    units: {
      mL: { factor: 1e-3, labelKey: 'units.volume.milliliters', abbrevKey: 'units.volume.millilitersAbbrev', aliases: ['milliliter', 'milliliters', 'millilitre', 'millilitres'] },
      L: { factor: 1, labelKey: 'units.volume.liters', abbrevKey: 'units.volume.litersAbbrev', aliases: ['liter', 'liters', 'litre', 'litres'] },
      // US customary. British imperial variants would be distinct symbols.
      'fl oz': { factor: 0.0295735295625, labelKey: 'units.volume.fluidOunces', abbrevKey: 'units.volume.fluidOuncesAbbrev', aliases: ['floz', 'fluid ounce', 'fluid ounces'] },
      pt: { factor: 0.473176473, labelKey: 'units.volume.pints', abbrevKey: 'units.volume.pintsAbbrev', aliases: ['pint', 'pints'] },
      qt: { factor: 0.946352946, labelKey: 'units.volume.quarts', abbrevKey: 'units.volume.quartsAbbrev', aliases: ['quart', 'quarts'] },
      gal: { factor: 3.785411784, labelKey: 'units.volume.gallons', abbrevKey: 'units.volume.gallonsAbbrev', aliases: ['gallon', 'gallons'] },
    },
  },

  temperature: {
    canonicalUnit: '°C',
    units: {
      '°C': { factor: 1, offset: 0, labelKey: 'units.temperature.celsius', abbrevKey: 'units.temperature.celsiusAbbrev', aliases: ['c', 'degc', 'celsius'] },
      // C = F·5/9 − 160/9  (e.g. 212°F → 100°C)
      '°F': { factor: 5 / 9, offset: -160 / 9, labelKey: 'units.temperature.fahrenheit', abbrevKey: 'units.temperature.fahrenheitAbbrev', aliases: ['f', 'degf', 'fahrenheit'] },
      K: { factor: 1, offset: -273.15, labelKey: 'units.temperature.kelvin', abbrevKey: 'units.temperature.kelvinAbbrev', aliases: ['kelvin'] },
    },
  },

  area: {
    canonicalUnit: 'm²',
    units: {
      'm²': { factor: 1, labelKey: 'units.area.squareMeters', abbrevKey: 'units.area.squareMetersAbbrev', aliases: ['m2', 'sqm', 'square meter', 'square meters', 'square metre', 'square metres'] },
      'ft²': { factor: 0.09290304, labelKey: 'units.area.squareFeet', abbrevKey: 'units.area.squareFeetAbbrev', aliases: ['ft2', 'sqft', 'square foot', 'square feet'] },
      acre: { factor: 4046.8564224, labelKey: 'units.area.acres', abbrevKey: 'units.area.acresAbbrev', aliases: ['acres', 'ac'] },
      ha: { factor: 10000, labelKey: 'units.area.hectares', abbrevKey: 'units.area.hectaresAbbrev', aliases: ['hectare', 'hectares'] },
      'km²': { factor: 1e6, labelKey: 'units.area.squareKilometers', abbrevKey: 'units.area.squareKilometersAbbrev', aliases: ['km2', 'sqkm', 'square kilometer', 'square kilometers'] },
      'mi²': { factor: 2589988.110336, labelKey: 'units.area.squareMiles', abbrevKey: 'units.area.squareMilesAbbrev', aliases: ['mi2', 'sqmi', 'square mile', 'square miles'] },
    },
  },
};

/** Category names with real runtime conversion support. */
export const SUPPORTED_UNIT_CATEGORIES: string[] = Object.keys(UNIT_CATEGORIES);
